import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { getLang, getPermissions } from "store/userSlice";
import { useDispatch, useSelector } from "react-redux";
import { api } from "helpers/axios";
import * as yup from "yup";
import { toast } from "helpers/toast";
import * as Sentry from "@sentry/react";
import { empty, errorMessage, fieldDecimalCheck, formatDateTime, formatFixedDigits, formatMoney, formatNumberTo, getTextFromHtml, isTrue, removeTrailingZeros, scrollInto } from "helpers/common";
import { en, de } from 'yup-locales';
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { getGroups } from "store/groupsSlice";
import { getBudgetSettings } from "store/budgetSettingsSlice";
import { getBalanceScorecardList } from "store/balanceScorecardSlice";
import 'react-quill/dist/quill.snow.css';
import sanitize from "helpers/sanitize";
import hasPermissionTo from "helpers/permission";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import TextTruncate from "components/ui/TextTruncate";
import ExpandableHtml from "components/ui/ExpandableHtml";
import { StateEmployeeField, stateEmployeeValidationObj } from "components/BalanceScorecard/DB-EmployeeDataMode";
import ScenarioExportAction from "components/BalanceScorecard/ScenarioExportAction";
import EmployeeTableColumn from "components/EmployeeManagement/EmployeeTableColumn";
import { getGeneralSettings } from "store/generalSettingsSlice";
import { getAllCustomNames } from "store/fieldsSlice";
import { getStateEmployeeSettings } from "store/stateEmployeeSlice";
import SettingName from "components/BalanceScorecard/Custom/DB/SettingName";
import GenderSelect from "components/EmployeeManagement/inputs/GenderSelect";
import { hasCanonicalGenderField, translateGenderValue } from "components/EmployeeManagement/inputs/genderOptions";
import EmployeeDeactivationModal from "components/EmployeeManagement/EmployeeDeactivationModal";
import { BASE_KEEP_KEYS, buildEmployeePayload } from "components/EmployeeManagement/employeePayload";
import { finalAssignmentConfirmationDetails, withFinalAssignmentConfirmation } from "helpers/employeeDeactivation";
import { canManageEmployeeAssignments } from "helpers/employeeAssignmentAuthorization";
import { Button, DateInput, Field, Input, InputAffix, NativeSelect, Select } from "components/ui";
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

function BalancedScorecard({ index, employee, data, total, fetchEmployees, balancedScorecardOpened, setBalancedScorecardOpened, handleCancelAddBalancedScorecard, permissions, setEmployeeLkDirty, getLabel, lkWeightTotal, evaluationFlowOptions, isInternalTenant }) {
  const supportedSettingGroupsForEvaluationFlow = ['regelbeurteilung', 'anlassbeurteilung'];
  const isCompleted = !!data?.evaluation_completed_at;

  const [editMode, setEditMode] = useState(false);

  const { translate, editableTranslate, editableTranslateString } = useTranslation();
  const lang = useSelector(getLang);

  const fieldCustomNames = useSelector(getAllCustomNames);

  const groups = useSelector(getGroups);
  const balancedScorecardList = useSelector(getBalanceScorecardList);
  const balancedScorecardSettingGroupOptions = useMemo(() => {
    if (!balancedScorecardList) return [];

    const groupsMap = new Map();
    balancedScorecardList.forEach((bs) => {
      const group = bs?.balanced_scorecard_setting_group;
      if (group?.hash_id && !groupsMap.has(group.hash_id)) {
        groupsMap.set(group.hash_id, { label: group.name, value: group.hash_id, slug: group.slug });
      }
    });

    return Array.from(groupsMap.values());
  }, [balancedScorecardList]);

  const balancedScorecardSettingGroupBySetting = useMemo(() => {
    const groupsMap = new Map();
    if (!balancedScorecardList) return groupsMap;

    balancedScorecardList.forEach((bs) => {
      const group = bs?.balanced_scorecard_setting_group;
      if (bs?.hash_id && group?.hash_id) {
        groupsMap.set(bs.hash_id, group.hash_id);
      }
    });

    return groupsMap;
  }, [balancedScorecardList]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const schema = yup.object({
    index: yup.number().nullable(true),
    completed: yup.boolean().nullable(true),
    balanced_scorecard_setting_group_hash_id: yup.string().required().label(fieldCustomNames?.['field.balanced_scorecard_template'] || translate('state_employee.rating_mode')),
    balanced_scorecard_setting_hash_id: yup.string().required().label(translate('state_employee.rating_mode.template')),
    group_hash_id: yup.string().required().label(fieldCustomNames?.['field.group'] || translate('field.group', 'Gruppe')),
    state_evaluation_flow: yup.string().notRequired().label(translate('state_employee.evaluation_flow', 'Beurteilung Ablauf'))
  });

  const { register, control, getValues, setValue, reset, watch, handleSubmit, formState: { isDirty, errors } } = useForm({
    resolver: yupResolver(schema)
  });

  const watchIndex = watch('index');

  const watchGroupHashId = watch('group_hash_id');
  const watchBalancedScorecardSettingGroup = watch('balanced_scorecard_setting_group_hash_id');
  const watchBalancedScorecardSetting = watch('balanced_scorecard_setting_hash_id');
  const watchStateEvaluationFlow = watch('state_evaluation_flow');

  const isInitialBalancedScorecardSettingGroup = useRef(true);

  const selectedBalancedScorecardSettingGroup = useMemo(() => {
    if (!watchBalancedScorecardSettingGroup) return null;

    for (const opt of balancedScorecardSettingGroupOptions) {
      if (opt.value === watchBalancedScorecardSettingGroup) {
        return opt.slug;
      }
    }

    return null;
  }, [balancedScorecardSettingGroupOptions, watchBalancedScorecardSettingGroup]);

  const balancedScorecardOptions = useMemo(() => {
    if (!balancedScorecardList) return [];

    return balancedScorecardList
      .filter(bs => bs?.form_definition?.type === 'custom_balanced_scorecard_setting')
      .filter(bs => bs?.balanced_scorecard_setting_group?.hash_id)
      .filter(bs => !watchBalancedScorecardSettingGroup || bs?.balanced_scorecard_setting_group?.hash_id === watchBalancedScorecardSettingGroup)
      .map(bs => ({
        label: bs?.balanced_scorecard_setting_group?.name
          ? bs.slug.replace(new RegExp(`^${bs.balanced_scorecard_setting_group.name}\\s*-\\s*`, 'i'), '')
          : bs.slug,
        value: bs.hash_id
      }));
  }, [balancedScorecardList, watchBalancedScorecardSettingGroup]);

  // The select needs the translated label; the raw slug stays in balancedScorecardOptions.
  const balancedScorecardSelectOptions = balancedScorecardOptions.map(
    opt => ({ value: opt.value, label: translate('state_employee.rating_mode.template_' + opt.label?.toLowerCase()) })
  );

  const defaultBalancedScorecardValue = useMemo(() => {
    if (!balancedScorecardList) return null;
    const obj = balancedScorecardList.find(bs => bs?.form_definition?.type === 'custom_balanced_scorecard_setting' && bs?.slug === 'regelbeurteilung-regular');
    return obj?.hash_id || null;
  }, [balancedScorecardList]);

  const defaultBalancedScorecardSettingGroup = useMemo(() => {
    if (!defaultBalancedScorecardValue) return '';
    return balancedScorecardSettingGroupBySetting.get(defaultBalancedScorecardValue) || '';
  }, [balancedScorecardSettingGroupBySetting, defaultBalancedScorecardValue]);

  useEffect(() => {
    setEmployeeLkDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    if (employee && !data) { // when adding a new balanced scorecard
      handleShowEditMode();
    }
  }, [data]);

  useEffect(() => {
    if (!watchGroupHashId) return;

    // remove all warning messages
    removeAllWarnings();

    // if group is changed, show warning message
    if (data?.group?.hash_id && data.group.hash_id !== watchGroupHashId) {
      // insert warning message into the DOM
      const warningMessage = document.createElement('div');
      warningMessage.className = 'alert alert-warning';
      warningMessage.id = `group-changed-warning-${data.hash_id}`;
      warningMessage.innerHTML = editableTranslateString('state_employee.group_changed_warning', 'Wird die Beurteilung einer anderen Gruppe zugewiesen, können die Führungskräfte der neuen Gruppe alle bisherigen Änderungen einsehen. Wenn dies nicht gewünscht ist, muss die Beurteilung gelöscht und neu erstellt werden.');
      const balancedScorecardElement = document.getElementById(`employee-balanced-scorecard-${data.hash_id}`);
      if (balancedScorecardElement) {
        balancedScorecardElement.prepend(warningMessage);
      }
    }

  }, [watchGroupHashId]);

  useEffect(() => {
    if (data || !employee?.employee_group?.hash_id) return;
    setValue('group_hash_id', employee.employee_group.hash_id);
  }, [data, employee?.employee_group?.hash_id, setValue]);

  useEffect(() => {
    if (isInitialBalancedScorecardSettingGroup.current) {
      isInitialBalancedScorecardSettingGroup.current = false;
      return;
    }

    if (!watchBalancedScorecardSetting) return;

    const settingGroupHashId = balancedScorecardSettingGroupBySetting.get(watchBalancedScorecardSetting);
    if (settingGroupHashId && settingGroupHashId === watchBalancedScorecardSettingGroup) return;

    setValue('balanced_scorecard_setting_hash_id', '');
  }, [balancedScorecardSettingGroupBySetting, setValue, watchBalancedScorecardSetting, watchBalancedScorecardSettingGroup]);

  useEffect(() => {
    if (watchBalancedScorecardSettingGroup || !watchBalancedScorecardSetting) return;

    const settingGroupHashId = balancedScorecardSettingGroupBySetting.get(watchBalancedScorecardSetting);
    if (settingGroupHashId) {
      setValue('balanced_scorecard_setting_group_hash_id', settingGroupHashId);
    }
  }, [balancedScorecardSettingGroupBySetting, setValue, watchBalancedScorecardSetting, watchBalancedScorecardSettingGroup]);

  const removeAllWarnings = () => {
    // remove all warning messages
    const warningMessages = document.querySelectorAll(`[id^="group-changed-warning-"]`);
    if (warningMessages.length > 0) {
      warningMessages.forEach((message) => {
        message.remove();
      });
    }
  };

  const handleShowEditMode = async (e = null) => {
    e?.preventDefault();

    removeAllWarnings();

    if (!data && !employee?.employee_group?.hash_id) {
      setError({
        message: editableTranslate('state_employee.employee_group_required', 'Bitte weisen Sie dem*der Beschäftigten vor der Erstellung einer Beurteilung eine Gruppe zu.'),
        type: 'warning'
      });
      setBalancedScorecardOpened(true);
      return;
    }

    if (balancedScorecardOpened) return;

    if (data?.hash_id && !await postLock(data?.hash_id)) return;

    setBalancedScorecardOpened(true);

    const defaultSettingHashId = data?.balanced_scorecard_setting?.hash_id || defaultBalancedScorecardValue || '';
    const defaultSettingGroupHashId = balancedScorecardSettingGroupBySetting.get(defaultSettingHashId) || defaultBalancedScorecardSettingGroup || '';

    reset({
      index,
      completed: (data?.evaluation_completed_at ? true : false),
      rating_mode: data?.rating_mode || 'normal_rating',
      alternative_result: data?.alternative_result || '',
      average_value_individual: data?.average_value_individual || null,
      hash_id: data?.hash_id || '',
      employee_hash_id: employee.hash_id,
      balanced_scorecard_setting_hash_id: defaultSettingHashId,
      budget_setting_hash_id: data?.budget_setting?.hash_id || '',
      group_hash_id: data?.group?.hash_id || employee?.employee_group?.hash_id || '',
      balanced_scorecard_setting_group_hash_id: defaultSettingGroupHashId,
      pay_factor_source: data?.pay_factor_source || 'calculation_setting',
      pay_factor: data?.pay_factor || '',
      distribution_percentage: data?.distribution_percentage || '',
      fixed_payout: data?.fixed_payout || '',
      state_evaluation_flow: data?.state_evaluation_flow || 'default'
    })

    setEditMode(true);
  };

  const handleSubmitBalancedScorecardAction = async (formData) => {
    if (isCompleted) return;

    if (!data && !employee?.employee_group?.hash_id) {
      setError({
        message: translate('state_employee.employee_group_required', 'Bitte weisen Sie dem*der Beschäftigten vor der Erstellung einer Beurteilung eine Gruppe zu.'),
        type: 'warning'
      });
      return;
    }

    setError(null);
    setLoading(true);

    removeAllWarnings();

    if (!supportedSettingGroupsForEvaluationFlow.includes(selectedBalancedScorecardSettingGroup)) {
      formData.state_evaluation_flow = null;
    }

    try {
      const { status } = await api.post('/employee-balanced-scorecards', formData);
      if (status === 200) {
        toast.success(translate('form.success.saved'));
        await handleCancel();
        fetchEmployees(false, false);
      }
      setLoading(false);
    } catch (err) {
      if (err?.response?.data?.message?.includes('Weighting error')) {
        setError({ translationKey: 'balance_scorecard.weighting_error' });
      } else {
        setError(errorMessage(err, 'save balanced scorecard setting'));
      }
      setLoading(false);
      Sentry.captureException(err);
    };
  };

  const handleReleaseBalancedScorecard = async () => {
    const formData = {
      completed: false
    };

    setError(null);
    setLoading(true);

    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${data?.hash_id}/evaluation`, formData);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        await fetchEmployees(false, false);
        await postUnlock(data?.hash_id);
      }
      setLoading(false);
    } catch (err) {
      setError(errorMessage(err, 'save balanced scorecard setting'));
      setLoading(false);
      Sentry.captureException(err);
    };
  };

  const handleCancel = async () => {
    if (!data) handleCancelAddBalancedScorecard();
    else {
      await postUnlock(data?.hash_id);
    }
    setEditMode(false);
    setBalancedScorecardOpened(false);
  };

  const onDeleteBalancedScorecard = async (hash_id) => {
    if (!await postLock(hash_id)) return;

    setError(null);

    try {
      const { status } = await api.delete(`/employee-balanced-scorecards/${hash_id}`);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        fetchEmployees(false, false);
      }
    } catch (err) {
      setError(errorMessage(err, 'delete balanced scorecard'));
      Sentry.captureException(err);
    }
  };

  const postLock = async (hashId) => {
    setError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/lock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('state_employee.lock_forbidden', 'Diese Beurteilung wird gerade von einem anderen Nutzenden bearbeitet: $X.');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setError(formatted);
      } else {
        setError(errorMessage(err, 'lock employee balanced scorecard'));
      }
    }
  };

  const postUnlock = async (hashId) => {
    setError(null);
    try {
      const { status } = await api.post(`/employee-balanced-scorecards/${hashId}/unlock`);
      if (status === 200) return true;
    } catch (err) {
      if (err?.response?.status === 403) {
        let formatted = translate('state_employee.lock_forbidden', 'Diese Beurteilung wird gerade von einem anderen Nutzenden bearbeitet: $X.');
        if (err?.response?.data) {
          const errData = err?.response?.data;
          if (errData?.data?.evaluator) {
            formatted = formatted.replace('$X', '<b>' + errData?.data?.evaluator?.name + '</b>');
          }
        }
        setError(formatted);
      } else {
        setError(errorMessage(err, 'unlock employee balanced scorecard'));
      }
    }
  };

  const handleUnlockComplaint = async (hashId) => {
    setError(null);

    try {
      const { status } = await api.post(`/employee-complaints/${hashId}/unlock`);
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        await fetchEmployees(false, false);
      }
    } catch (err) {
      setError(errorMessage(err, 'unlock employee complaint'));
      Sentry.captureException(err);
    }
  };

  return <div className={`employee-balancescorecard-card position-relative ${data?.evaluation_completed_at ? 'completed' : ''} ${data?.exclude_from_payout ? 'exclude-from-payout' : ''} mb-3`} data-tooltip-id={employee.exclude_from_payout ? `employee-exclusion-tooltip` : (data?.exclude_from_payout ? `balanced-scorecard-exclusion-tooltip` : '')}>
    <ReactTooltip id={`lk-full-name-tooltip`} place="bottom" />
    <ReactTooltip id={`employee-exclusion-tooltip`} content={translate('tooltips.balanced_scorecard.employee_exclude_from_payout_tooltip')} />
    <ReactTooltip id={`balanced-scorecard-exclusion-tooltip`} content={translate('tooltips.state_employee.exclude_from_payout_tooltip', 'Die Beurteilung ist aktiv, allerdings liegen Ausschlussgründe vor (z. B. Fehlzeiten). Falls mehrere Beurteilungen angelegt wurden, bleibt die Gewichtung unverändert bestehen.')} />
    <form onSubmit={handleSubmit(handleSubmitBalancedScorecardAction)} data-testid="scorecard-form">
      <div className="card-setting-header d-flex justify-content-between align-items-center">
        <span>
          {data ? <SettingName setting={data?.balanced_scorecard_setting} className="ws-normal" /> : ''}
          {
            data?.rating_mode === 'average_value_percentile' && <>
              <i className="bi bi-slash-circle-fill green-color ml-2 mr-1" data-tooltip-id="lk-average_value_percentile-tooltip"></i>
              <ReactTooltip id={`lk-average_value_percentile-tooltip`} place="bottom" content={translate('tooltips.evaluation.lk.average_tooltip')} />
            </>
          }
          {
            data && <sup>
              <i className="bi bi-clock ml-1" data-tooltip-id="lk-last-updated-tooltip" data-tooltip-content={`${translate('tooltips.balanced_scorecard.last_updated')} : ${data?.has_evaluation_started ? formatDateTime(data?.updated_at) : '-'}`}></i>
            </sup>
          }
        </span>
        {
          (!editMode && data) ? <div>
            {
              !data.exclude_from_payout && <>
                {
                  (data.evaluation_completed_at || data.initial_evaluation_completed_at) ? <>
                    {
                      data.evaluation_completed_at && <>
                        <i className="bi bi-check-circle-fill v-middle mr-2" data-tooltip-id={`balanced-scorecard-completed-${data.hash_id}`}></i>
                        <ReactTooltip id={`balanced-scorecard-completed-${data.hash_id}`} place="bottom" content={translate('state_employee.rating_card_completed') + ' (' + formatDateTime(data.evaluation_completed_at) + ')'} />
                      </>
                    }
                    {
                      (!data.evaluation_completed_at && data?.initial_evaluation_completed_at) && <>
                        <i className="bi bi-check-circle orange-color v-middle mr-2" data-tooltip-id={`balanced-scorecard-initial-completed-${data.hash_id}`}></i>
                        <ReactTooltip id={`balanced-scorecard-initial-completed-${data.hash_id}`} place="bottom" content={translate('state_employee.initial_evaluation_completed') + ' (' + formatDateTime(data.initial_evaluation_completed_at) + ')'} />
                      </>
                    }

                    {
                      hasPermissionTo('employee_management.manage_balanced_scorecards.release', permissions) && <Button variant="ghost" size="inline" icon="bi-unlock" loading={loading} id={`release-balanced-scorecard-${data.hash_id}`} data-tooltip-id="release-balanced-scorecard-tooltip" data-testid="release-button" />
                    }

                    <ReactTooltip id="release-balanced-scorecard-tooltip" content={translate(data.initial_evaluation_completed_at ? 'state_employee.release_rating_card' : 'tooltips.state_employee.release_button', 'Beurteilung freigeben')} />

                    {
                      !loading && <ReactTooltip anchorSelect={`#release-balanced-scorecard-${data.hash_id}`} place="bottom" openOnClick={true} clickable>
                        <div style={{ maxWidth: '380px', whiteSpace: 'normal' }} className="p-1">
                          <div className="mb-2 font-weight-bold">
                            {editableTranslate('state_employee.reopen_evaluation_title', 'Beurteilung wieder öffnen?')}
                          </div>
                          <div className="mb-3">
                            {editableTranslate('state_employee.reopen_evaluation_confirmation', 'Beim Wiederöffnen wird der Abschlussstatus zurückgesetzt. Bereits eingegebene Bewertungsdaten bleiben erhalten. Der gespeicherte Stammdaten-Kontext wird entfernt und die Beurteilung verwendet wieder die aktuellen Beschäftigtendaten. Möchten Sie fortfahren?')}
                          </div>
                          <Button variant="primary" size="sm" onClick={handleReleaseBalancedScorecard} data-testid="reopen-button" className="font-weight-normal">{editableTranslate('button.reopen', 'Wieder öffnen')}</Button>
                        </div>
                      </ReactTooltip>
                    }
                  </> :
                    <>
                      {
                        hasPermissionTo('employee_management.manage_balanced_scorecards.delete', permissions) && <Button variant="ghost" size="inline" tone="danger" icon="bi-trash" id={`delete-balanced-scorecard-${data.hash_id}`} className={!hasPermissionTo('employee_management.manage_balanced_scorecards.create', permissions) ? 'mr-2' : ''} data-testid="delete-button" />
                      }

                      <ReactTooltip className="opacity-1" anchorSelect={`#delete-balanced-scorecard-${data.hash_id}`} place="bottom" openOnClick={true} clickable>
                        <Button variant="primary" size="sm" onClick={() => onDeleteBalancedScorecard(data.hash_id)} data-testid="confirm-button" className="font-weight-normal">{editableTranslate('form.delete.yes')}</Button>
                      </ReactTooltip>
                    </>
                }
                <ScenarioExportAction
                  isInternalTenant={isInternalTenant}
                  balancedScorecardHashId={data?.hash_id}
                  settingName={data?.balanced_scorecard_setting?.name || data?.balanced_scorecard_setting?.slug}
                />
                {
                  hasPermissionTo('employee_management.manage_balanced_scorecards.create', permissions) && <Button variant="ghost" size="inline" icon="bi-pencil-square" onClick={handleShowEditMode} data-testid="edit-button" />
                }
              </>
            }
          </div> : <div>
            <Button variant="secondary" onClick={handleCancel} className="mr-2" data-testid="cancel-button">
              <span className="hidden-xl">{editableTranslate('button.cancel')}</span>
              <i className="bi bi-x-lg text-normal show-xl" data-tooltip-id="form-cancel-tooltip"></i>
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={loading}
              disabled={isCompleted}
              title={isCompleted ? translate('state_employee.completed_field_requires_reopen', 'Geben Sie die Beurteilung erneut frei, um Änderungen vorzunehmen.') : undefined} data-testid="save-button"
            >
              <span className="hidden-xl">{editableTranslate('button.save')}</span>
              {!loading && <i className="bi bi-floppy text-normal show-xl" data-tooltip-id="form-save-tooltip"></i>}
            </Button>
          </div>
        }
      </div>
      <hr className="mt-2" />
      <div className="card-setting-body mt-3" id={`employee-balanced-scorecard-${data?.hash_id}`}>
        {
          error && <div className="pb-2">
            <ErrorAlert error={error} isHtml={true} onClose={() => setError(null)} type={error?.type} />
          </div>
        }
        {
          (!editMode && data) ? <div className="row">
            <div className="col-12 d-flex justify-content-between p-0">
              <div className="col card-body-key">{fieldCustomNames?.['field.group'] || editableTranslate('field.group')}</div>
              <div className="col card-body-value">{data?.group?.name || <i className="bi bi-exclamation-triangle-fill orange-color" data-tooltip-id={'no-group-db-tooltip'}></i>}</div>
            </div>
            {
              data.complaints && data.complaints.data.map((complaint, i) => <Fragment key={i}>
                <div className="col-10 d-flex justify-content-between p-0">
                  <div className="col card-body-key">{editableTranslate('field.complaint', 'Beschwerde')}</div>
                  <div className="col card-body-value">
                    <TextTruncate text={getTextFromHtml(sanitize(complaint?.complaint_notes))} className="pr-5" />
                  </div>
                </div>
                <div className="col-2 text-right">
                  {
                    complaint.is_resolved && <i className="bi bi-check-circle-fill v-middle green-color" data-tooltip-id={`complaint-resolved-tooltip`} data-tooltip-content={translate('tooltips.complaint.resolved_at', 'Beschwerde beigelegt am') + ' ' + formatDateTime(complaint.resolved_at, true)} />
                  }
                  {
                    !complaint.is_resolved && complaint.is_locked && <Button variant="ghost" size="inline" className="text-default" icon="bi-unlock" data-tooltip-id={`complaint-unlock`} onClick={() => handleUnlockComplaint(complaint.hash_id)} data-testid={`unlock-complaint-${complaint.hash_id}`} />
                  }
                  {
                    // !complaint.is_resolved && !complaint.is_locked && <span className={`text-default font-weight-bold py-0`}><i className="bi bi-unlock"></i></span>
                  }
                </div>
              </Fragment>)
            }
          </div> : <div>
            {
              isCompleted && <div className="alert alert-info ws-normal" role="status">
                <i className="bi bi-info-circle-fill mr-2" aria-hidden="true"></i>
                {editableTranslate(
                  'state_employee.completed_read_only_info',
                  'Diese Beurteilung ist abgeschlossen und schreibgeschützt. Geben Sie sie erneut frei, bevor Sie Änderungen vornehmen.'
                )}
              </div>
            }
            <div className="form-row align-items-start">
              <div className="form-group col">
                <label className="peb-field__label">{getLabel('field.group', 'Group', fieldCustomNames?.['field.group'])}</label>
                <input type="hidden" {...register('group_hash_id')} data-testid="group-hash-id-input" />
                <div className={`form-control-plaintext ${errors?.group_hash_id ? 'is-invalid' : ''}`}>
                  {data?.group?.name || employee?.employee_group?.name || '-'}
                </div>
                {errors?.group_hash_id && <div className="invalid-feedback">{errors.group_hash_id.message}</div>}
              </div>
            </div>
            <div className="form-row align-items-start">
              <div className="form-group col">
                <Field label={getLabel('state_employee.rating_mode', 'Rating mode', fieldCustomNames?.['field.balanced_scorecard_template'])} error={errors?.balanced_scorecard_setting_group_hash_id?.message}>
                <div {...data?.has_evaluation_started ? { 'data-tooltip-id': `bs-balanced_scorecard-template-locked-db-tooltip` } : {}}>
                  <Controller
                    name={'balanced_scorecard_setting_group_hash_id'}
                    control={control}
                    render={({ field: f }) =>
                      <div style={{ display: 'contents' }} data-testid="balanced-scorecard-setting-group-hash-id-select">
                        <Select
                          {...f}
                          error={!!errors?.balanced_scorecard_setting_group_hash_id}
                          options={balancedScorecardSettingGroupOptions}
                          value={balancedScorecardSettingGroupOptions.find(o => o.value === watchBalancedScorecardSettingGroup) || null}
                          onChange={(opt) => f.onChange(opt?.value || '')}
                          placeholder={translate('form.select_placeholder')}
                          isClearable={!data?.has_evaluation_started}
                          isDisabled={data?.has_evaluation_started}
                        />
                      </div>
                    }
                  />
                </div>
                </Field>
              </div>
            </div>
            {
              watchBalancedScorecardSettingGroup && <div className="form-row align-items-start">
                <div className="form-group col">
                  <Field label={getLabel('state_employee.rating_mode.template', 'Template')} error={errors?.balanced_scorecard_setting_hash_id?.message}>
                  <div {...data?.has_evaluation_started ? { 'data-tooltip-id': `bs-balanced_scorecard-template-locked-db-tooltip` } : {}}>
                    <Controller
                      name={'balanced_scorecard_setting_hash_id'}
                      control={control}
                      render={({ field: f }) =>
                        <div style={{ display: 'contents' }} data-testid="balanced-scorecard-setting-hash-id-select">
                          <Select
                            {...f}
                            error={!!errors?.balanced_scorecard_setting_hash_id}
                            options={balancedScorecardSelectOptions}
                            value={balancedScorecardSelectOptions.find(o => o.value === watchBalancedScorecardSetting) || null}
                            onChange={(opt) => f.onChange(opt?.value || '')}
                            placeholder={translate('form.select_placeholder')}
                            isClearable={!data?.has_evaluation_started}
                            isDisabled={data?.has_evaluation_started}
                          />
                        </div>
                      }
                    />
                  </div>
                  </Field>
                </div>
              </div>
            }
            {
              (evaluationFlowOptions.length > 0 && supportedSettingGroupsForEvaluationFlow.includes(selectedBalancedScorecardSettingGroup)) && <div className="form-row align-items-start">
                <div className="form-group col">
                  <Field label={editableTranslate('state_employee.evaluation_flow', 'Beurteilung Ablauf')}>
                  <NativeSelect {...register('state_evaluation_flow')} disabled={data?.evaluation_completed_at ? true : false} data-tooltip-id={data?.evaluation_completed_at ? `evaluation-flow-${index}-tooltip` : null} data-testid="state-evaluation-flow-select">
                    {
                      evaluationFlowOptions.map((opt, i) => <option key={i} value={opt}>{translate('state_employee.evaluation_flow.' + opt, opt)}</option>)
                    }
                  </NativeSelect>
                  </Field>
                </div>
                {
                  data?.evaluation_completed_at && <ReactTooltip id={`evaluation-flow-${index}-tooltip`} place="bottom" content={translate('state_employee.evaluation_flow.' + watchStateEvaluationFlow)} />
                }
              </div>
            }
          </div>
        }
      </div>
    </form>
  </div>
}

export default function EmployeeRow({
  employee,
  tableFields = null,
  regularEvaluationLevels,
  fixedFieldsData,
  systemFieldsData,
  freeFieldsData,
  calculationRelatedFieldsData,
  calculationRelatedFields,
  conditionalFieldsData,
  stateEmployeeSections,
  fetchEmployees,
  selectedEmployee,
  setSelectedEmployee,
  softwareSettings,
  handleCancelAddEmployee,
  isInternalTenant,
  sortCols,
  setEmployeeDirty,
  setEmployeeLkDirty,
  resizedCount = null,
  noDetails = false,
  getLabel,
  userRoles,
  maxBalancedScorecard,
  allFields
}) {
  const dispatch = useDispatch();

  const lang = useSelector(getLang);

  const { translate, editableTranslate } = useTranslation();
  const canManageAssignments = canManageEmployeeAssignments(userRoles, 'db');

  const fieldCustomNames = useSelector(getAllCustomNames);

  const permissions = useSelector(getPermissions);

  const generalSettings = useSelector(getGeneralSettings);

  const stateEmployeeSettings = useSelector(getStateEmployeeSettings);

  const [showEmployeeDataEditForm, setShowEmployeeDataEditForm] = useState(false);

  const [employeeBalancedScorecards, setEmployeeBalancedScorecards] = useState([]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const [balancedScorecardOpened, setBalancedScorecardOpened] = useState(false);

  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [loadingDeactivate, setLoadingDeactivate] = useState(false);
  const [finalAssignmentCount, setFinalAssignmentCount] = useState(null);

  const groups = useSelector(getGroups);

  const evaluationFlowOptions = useMemo(() => {
    return stateEmployeeSettings?.state_employee_evaluation_flows || [];
  }, [stateEmployeeSettings]);

  const employmentDateBoth = useMemo(() => {
    if (fixedFieldsData) {
      const filtered = fixedFieldsData.filter(f => ['employment_entry_date', 'employment_exit_date'].includes(f.slug));
      if (filtered.length === 2) return true;
    }
    return false;
  }, [fixedFieldsData]);

  const hasCanonicalGender = useMemo(
    () => hasCanonicalGenderField(fixedFieldsData),
    [fixedFieldsData]
  );

  const defaultInfos = useMemo(() => {
    const temp = {};
    if (!employee && !employee?.employeeInformation) return temp;

    employee.employeeInformation.data.forEach(field => {
      temp[field.key] = field.value;
    });
    return temp;
  }, [employee]);

  const getEmployeeInformationMeta = (identifier) => {
    return employee?.employeeInformation?.data?.find((item) => item?.key === identifier) || null;
  };

  const lkWeightTotal = useMemo(() => {
    let total = 0;
    employeeBalancedScorecards.forEach(lk => {
      if (lk) {
        total += +formatNumberTo(lk.distribution_percentage, '.');
      }
    });
    return total;
  }, [employeeBalancedScorecards]);

  const stateEmployeeYupObj = useMemo(() => {
    const fields = (stateEmployeeSections) ? stateEmployeeValidationObj(stateEmployeeSections, userRoles, translate) : {};
    if (hasCanonicalGender) delete fields.gender;
    return fields;
  }, [stateEmployeeSections, hasCanonicalGender]);

  const schema = useMemo(() => {
    const formatFields = {};
    if (calculationRelatedFields) {
      calculationRelatedFields.filter(f => f.decimal !== null).forEach(f => {
        formatFields[f.slug] = f;
      });
    }

    const freeFieldsObj = {};
    if (freeFieldsData) {
      for (const f of freeFieldsData) {
        if (f?.source !== 'state_employee_config') {
          freeFieldsObj[f?.name] = yup.string().notRequired().max(255).label(f?.custom_name || translate(f?.translation_key, f?.name));
        }
      }
    }

    return yup.object({
      last_name: yup.string().required().label(fieldCustomNames?.['field.last_name'] || translate('field.last_name')),
      first_name: yup.string().required().label(fieldCustomNames?.['field.first_name'] || translate('field.first_name')),
      employee_number: yup.string().required().label(fieldCustomNames?.['field.employee_number'] || translate('field.employee_number')),
      uses_individual_pay_factor: yup.boolean().notRequired(),
      individual_pay_factor: yup.string().transform(cv => cv ? cv : '').when('uses_individual_pay_factor', {
        is: true,
        then: (schema) => schema.required().test('positive-number', translate('form.positive_number_required', 'Bitte einen positiven Wert eingeben.'), (value) => Number(String(value).replace(',', '.')) > 0),
        otherwise: (schema) => schema.notRequired(),
      }).label(fieldCustomNames?.['field.individual_pay_factor'] || translate('field.individual_pay_factor', 'individueller Entgeltfaktor')),
      email: yup.string().email().notRequired().label(fieldCustomNames?.['field.email'] || translate('field.email')),
      employment_entry_date: yup.string().notRequired().label(fieldCustomNames?.['field.employment_entry_date'] || translate('field.employment_entry_date'))
        .test('valid-date', 'Invalid Date', val => {
          if (!val || val.trim() === '') return true;

          return dayjs(val, 'DD.MM.YYYY').isValid();
        }),
      employment_exit_date: yup.string().notRequired().label(fieldCustomNames?.['field.employment_exit_date'] || translate('field.employment_exit_date')).test('valid-exit-date', translate('employee_management.exit_date_cannot_before_entry_date', 'Das Austrittsdatum darf nicht vor dem Eintrittsdatum liegen.'), (val, ctx) => {
        if (!val || val.trim() === '') return true;
        else {
          const ex = dayjs(val, 'DD.MM.YYYY');
          if (ex.isValid() && ex.isSameOrAfter(dayjs(ctx.parent.employment_entry_date, 'DD.MM.YYYY'))) return true;
        }
      }),
      annual_compensation: yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .label(fieldCustomNames?.['field.annual_compensation'] || translate('field.annual_compensation')),
      missing_months: yup.string().transform(cv => cv ? cv : '').notRequired()
        .label(fieldCustomNames?.['field.missing_months'] || translate('field.missing_months')),
      working_days_per_week: yup.string().transform(cv => cv ? cv : '').notRequired()
        .label(fieldCustomNames?.['field.working_days_per_week'] || translate('field.working_days_per_week')),
      missing_days: yup.string().transform(cv => cv ? cv : '').notRequired()
        .label(fieldCustomNames?.['field.missing_days'] || translate('field.missing_days')),
      exclude_from_payout_reason: yup.string().notRequired(),
      manual_average_value: yup.string().notRequired(),
      trend: yup.string().notRequired().label(fieldCustomNames?.['field.trend'] || translate('field.trend')),
      pay_grade: yup.string().notRequired().label(fieldCustomNames?.['field.pay_grade'] || translate('field.pay_grade')),
      pay_grade_level: yup.string().notRequired().label(fieldCustomNames?.['field.pay_grade_level'] || translate('field.pay_grade_level')),
      gender: yup.string().nullable().max(255).label(fieldCustomNames?.['field.gender'] || translate('field.gender', 'Gender')),
      employee_group_hash_id: yup.string().notRequired().label(fieldCustomNames?.['field.employee_group'] || translate('field.employee_group')),
      is_example: yup.boolean().notRequired(),
      individual_ceiling: yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .label(fieldCustomNames?.['field.individual_ceiling'] || translate('field.individual_ceiling')),
      individual_average_rating: yup.string().notRequired()
        .transform(cv => cv ? cv : '')
        .label(fieldCustomNames?.['field.individual_average_rating'] || translate('field.individual_average_rating')),
      employee_information: yup.object().shape({
        ...freeFieldsObj
      }).notRequired(),
      ...stateEmployeeYupObj
    });
  }, [calculationRelatedFields, freeFieldsData, stateEmployeeYupObj]);

  const { register, handleSubmit, getValues, setValue, control, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      uses_individual_pay_factor: false
    }
  });

  const watchExcludeFromPayout = watch('exclude_from_payout');
  const watchTrend = watch('trend');
  const watchUsesIndividualPayFactor = watch('uses_individual_pay_factor');
  const watchGender = watch('gender');

  const allValues = watch();

  const stateEmployeeSectionsFiltered = useMemo(() => {
    if (stateEmployeeSections) {
      const displayedFields = allFields ? allFields.filter(f => f.display_in.includes('employee_data')).map(f => f.translation_key) : [];

      return stateEmployeeSections.reduce((acc, section) => {
        if (!section?.is_active || section?.identifier === 'state_employee_free_fields') return acc;

        const filteredFields = section.fields.reduce((acc2, f) => {
          if (hasCanonicalGender && f?.identifier === 'gender') return acc2;
          let showConditional = true;
          if (f?.show_conditional) {
            const val = allValues?.[f?.show_conditional?.identifier] || employee?.information?.[f?.show_conditional?.identifier];

            if (typeof f?.show_conditional?.equals === 'boolean') {
              showConditional = (f?.show_conditional?.equals === isTrue(val));
            } else {
              showConditional = (val === f?.show_conditional?.equals);
            }
          }

          const active = f?.is_active && showConditional && (f?.can_write_roles?.includes('application-admin') || f?.can_write_roles?.includes('hr'));

          if (!active) return acc2;

          if (f?.field_type === 'compound') {
            const newComponents = [];
            let hasLayout = false;
            f?.components?.forEach(component => {
              if (component?.type === 'layout') {
                hasLayout = true;
                component?.columns?.forEach(col => {
                  col?.components?.forEach(component2 => {
                    if (component2?.type === 'input' && (component2?.can_write_roles?.includes('application-admin') || component2?.can_write_roles?.includes('hr'))) {
                      if (displayedFields.includes(component2?.translation_key)) {
                        newComponents.push({
                          ...component2,
                          field_type: component2.input_type
                        });
                      }
                    }
                  });
                });
              } else if (displayedFields.includes(component?.translation_key)) {
                newComponents.push(component);
              }
            });
            const obj = {
              ...f,
              components: newComponents
            };
            if (hasLayout) {
              newComponents.forEach(nc => {
                acc2.push(nc);
              });
            } else {
              acc2.push(obj);
            }
          } else if (displayedFields.includes(f?.translation_key)) {
            acc2.push(f);
          }

          return acc2;
        }, []);

        if (filteredFields.length > 0) {
          acc.push({
            ...section,
            fields: filteredFields
          });
        }

        return acc;
      }, []);
    }
    return [];
  }, [stateEmployeeSections, allValues, employee, allFields, hasCanonicalGender]);

  const resultAsNumeric = useMemo(() => {
    if (!generalSettings) return false;

    // generalSettings is an array of object, search the key 'balanced_scorecard_result_format'
    const setting = generalSettings.find(s => s.key === 'balanced_scorecard_result_format');
    if (setting && setting.value === 'numeric') {
      return true;
    }

    return false;
  }, [generalSettings]);


  useEffect(() => {
    yup.setLocale(lang === 'en' ? en : de);
  }, [lang]);

  useEffect(() => {
    setEmployeeDirty(isDirty);
  }, [isDirty]);

  useEffect(() => {
    if (employee) {
      setEmployeeBalancedScorecards(employee.balanced_scorecards.data);
    } else {
      setShowEmployeeDataEditForm(true);
    }
    setBalancedScorecardOpened(false);
  }, [employee]);

  useEffect(() => {
    setEmployeeLkDirty(false);
  }, [balancedScorecardOpened]);

  useEffect(() => {
    if (error) {
      const timeoutId = setTimeout(() => {
        if (employee) scrollInto('.selected-employee-row', false, -100);
        else scrollInto('.employee-new', false, -100);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [error]);

  const getLabeledValue = (value) => {
    if (empty(value)) return '-';

    let tempValue = value;
    if (typeof tempValue === 'string') tempValue = parseFloat(formatNumberTo(tempValue, '.'));

    if (resultAsNumeric) return formatNumberTo(tempValue);

    for (let i = regularEvaluationLevels.length - 1; i >= 0; i--) {
      const lvl = regularEvaluationLevels[i].level;
      const val = parseFloat(formatNumberTo(regularEvaluationLevels[i].value, '.'));
      if (val % 1 === 0.5) {
        if (tempValue === val) return lvl;
      } else if (Math.abs(val - tempValue) < 0.5) return lvl;
    }

    return '-';
  };

  const clearFormAndCallCancel = () => {
    reset();

    if (!employee) {
      setSelectedEmployee(null);
      handleCancelAddEmployee();
    }

    setShowEmployeeDataEditForm(false);
  };

  const handleClick = () => {
    if (selectedEmployee === employee.hash_id) {
      setSelectedEmployee(null);
    } else {
      setSelectedEmployee(employee.hash_id);
    }
    setBalancedScorecardOpened(false);
    clearFormAndCallCancel();
  };

  const handleEmployeeDataAction = (e, employee) => {
    e?.currentTarget?.blur();
    e?.preventDefault();

    const stateEmployeeObj = {};
    for (const inf in defaultInfos) {
      let found = false;
      for (const ff of freeFieldsData) {
        if (ff.slug === inf && ff?.source !== 'state_employee_config') {
          found = true;
          break;
        }
      }
      if (found) continue;
      stateEmployeeObj[inf] = defaultInfos[inf];
    }

    for (const key in stateEmployeeYupObj) {
      const meta = stateEmployeeYupObj[key].describe().meta;
      if (meta?.stored_in_table === 'employees') {
        stateEmployeeObj[key] = employee?.[key];

        if ((meta?.type === 'integer' || meta?.type === "float" || meta?.type === "numeric" || meta?.type.includes("decimal")) && stateEmployeeObj[key]) {
          stateEmployeeObj[key] = removeTrailingZeros(stateEmployeeObj[key]);
        }
      }
    }

    stateEmployeeObj.previous_evaluation_results = employee?.previous_evaluation_results?.regular_evaluation?.result || null;
    if (stateEmployeeObj.previous_evaluation_results) {
      stateEmployeeObj.previous_evaluation_results = removeTrailingZeros(stateEmployeeObj.previous_evaluation_results);
    }

    reset({
      last_name: employee?.last_name,
      first_name: employee?.first_name,
      email: employee?.email,
      employee_number: employee?.employee_number,
      individual_pay_factor: removeTrailingZeros(employee?.individual_pay_factor),
      uses_individual_pay_factor: !!employee?.uses_individual_pay_factor,
      working_days_per_week: removeTrailingZeros(employee?.working_days_per_week),
      employment_entry_date: employee?.employment_entry_date,
      employment_exit_date: employee?.employment_exit_date,
      employee_information: { ...defaultInfos },
      annual_compensation: removeTrailingZeros(employee?.annual_compensation),
      missing_months: removeTrailingZeros(employee?.missing_months),
      missing_days: removeTrailingZeros(employee?.missing_days),
      pay_grade: employee?.pay_grade,
      pay_grade_level: employee?.pay_grade_level,
      gender: employee?.gender || null,
      employee_group_hash_id: employee?.employee_group?.hash_id || '',
      exclude_from_payout: employee?.exclude_from_payout,
      exclude_from_payout_reason: employee?.exclude_from_payout_reason,
      trend: employee?.trend || 'no',
      manual_average_value: employee?.manual_average_value,
      is_example: employee?.is_example,
      individual_ceiling: removeTrailingZeros(employee?.individual_ceiling),
      individual_average_rating: removeTrailingZeros(employee?.individual_average_rating),
      ...stateEmployeeObj
    });

    setShowEmployeeDataEditForm(!showEmployeeDataEditForm);
  };

  const payloadFields = { fixedFieldsData, calculationRelatedFieldsData, conditionalFieldsData, systemFieldsData, freeFieldsData };

  const formatErrorMessage = (err) => {
    let formatted = errorMessage(err, 'save employee');
    if (err?.response?.data) {
      const errData = err?.response?.data;
      if (errData?.data?.existing_employee_number) {
        const conflict = errData.data.existing_employee_number;
        const hasAssignedModule = Array.isArray(conflict.assigned_modules)
          ? conflict.assigned_modules.length > 0
          : Boolean(conflict.module);
        if (!hasAssignedModule) {
          formatted = translate('message.error.employee_number_already_exists_inactive');
        } else {
          formatted = translate('message.error.employee_number_already_exists');
        }
        formatted = formatted.replace('$X', '<b>' + errData?.data?.existing_employee_number?.name + '</b>');
      } else if (errData?.data?.existing_email_and_employee_number) {
        const obj = errData?.data?.existing_email_and_employee_number;
        formatted = translate('message.error.email_and_employee_number_belong_to_different_employees')
          .replace('$X', '<b>' + obj?.email?.name + '</b>')
          .replace('$Y', '<b>' + obj?.employee_number?.name + '</b>');
      } else if (errData?.data?.existing_email) {
        formatted = translate('message.error.email_already_exists')
          .replace('$X', '<b>' + errData?.data?.existing_email?.name + '</b>');
      }
    }
    return formatted;
  };

  const onSubmit = async (data) => {
    if (!employee) return handleCreateNewEmployee(data);

    setError(null);
    setLoading(true);

    const formData = buildEmployeePayload(data, payloadFields, {
      stateEmployeeYupObj,
      keepKeys: [...BASE_KEEP_KEYS, 'uses_individual_pay_factor', 'individual_pay_factor'],
      skipStateKeysInInformation: true,
    });

    try {
      const { status } = await api.put(`/employees/${employee.hash_id}`, formData);
      if (status === 200) {
        fetchEmployees(false, false);

        setLoading(false);
        setShowEmployeeDataEditForm(false);
        reset();

        toast.success(translate('form.success.saved'));
      }
    } catch (err) {
      setLoading(false);
      setShowEmployeeDataEditForm(true);
      setError(formatErrorMessage(err));
      Sentry.captureException(err);
    }
  };

  const handleCreateNewEmployee = async (data) => {
    setError(null);
    setLoading(true);

    const formData = buildEmployeePayload(data, payloadFields, {
      stateEmployeeYupObj,
      filterCalculationFields: false,
      keepKeys: [...BASE_KEEP_KEYS, 'uses_individual_pay_factor'],
    });

    try {
      const { status } = await api.post(`/employees/new`, formData);
      if (status === 200) {
        handleCancelAddEmployee();

        setLoading(false);

        toast.success(translate('form.success.saved'));

        await fetchEmployees(false, false);
      }
    } catch (err) {
      setLoading(false);
      setError(formatErrorMessage(err));
      Sentry.captureException(err);
    }
  };

  const handleAddBalancedScorecard = () => {
    if (!balancedScorecardOpened) setEmployeeBalancedScorecards([...employeeBalancedScorecards, null]);
  };

  const handleCancelAddBalancedScorecard = () => {
    setEmployeeBalancedScorecards(employeeBalancedScorecards.filter(bs => bs));
  };

  const openDeactivateModal = () => {
    setError(null);
    setFinalAssignmentCount(null);
    setShowDeactivateModal(true);
  };

  const closeDeactivateModal = () => {
    if (loadingDeactivate) return;
    setFinalAssignmentCount(null);
    setShowDeactivateModal(false);
  };

  const handleEmployeeDeactivate = async (confirmFinalAssignment = false) => {
    if (!employee) return;

    setLoadingDeactivate(true);
    setError(null);

    try {
      const payload = confirmFinalAssignment
        ? withFinalAssignmentConfirmation({})
        : {};
      const { status } = await api.post(`/employees/${employee?.hash_id}/deactivate`, payload);
      if (status === 200) {
        toast.success(translate('employee_management.deactivate_success', 'Die Person wurde in dieser Periode deaktiviert.'));

        setFinalAssignmentCount(null);
        setShowDeactivateModal(false);

        await fetchEmployees(false, false);
      }
    } catch (err) {
      const confirmation = finalAssignmentConfirmationDetails(err);
      if (confirmation && !confirmFinalAssignment) {
        setFinalAssignmentCount(confirmation.count || 1);
      } else {
        if (confirmFinalAssignment) {
          setFinalAssignmentCount(null);
          setShowDeactivateModal(false);
        }
        setError(errorMessage(err, 'deactivate employee in period'));
        Sentry.captureException(err);
      }
    }

    setLoadingDeactivate(false);
  };

  return <>
    <tr className="tr-separator"></tr>
    {
      employee && <tr className={`table-row ${selectedEmployee === employee.hash_id ? 'selected-employee-row' : ''} ${noDetails ? 'no-select' : ''}`} onClick={noDetails ? null : handleClick} data-testid={`employee-row-${employee.hash_id}`}>
        {
          !noDetails && <td className="text-center v-middle w-1px"><i className={`open-row-icon bi bi-chevron-${selectedEmployee === employee.hash_id ? 'up' : 'down'}`}></i></td>
        }
        {
          tableFields.map((field, i) => <EmployeeTableColumn key={i} field={field} employee={employee} sortCols={sortCols} forEmployeeManagement={true} resizedCount={resizedCount} />)
        }
      </tr>
    }
    {
      (!employee || selectedEmployee === employee.hash_id) && <tr className={`employee-detail-row ${!employee ? 'employee-new' : ''}`} data-testid="employee-detail">
        <td colSpan={16}>
          <div className="employee-detail-div">
            {
              error && <div className="pb-2">
                <ErrorAlert error={error} isHtml={true} onClose={() => setError(null)} />
              </div>
            }

            <div className="row flex-nowrap">
              <div className={`col-auto employee-data-card-col`}>
                <div className="h-100">
                  <form onSubmit={handleSubmit(onSubmit)} className="employee-data-card" data-testid="employee-form">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h4 className="mb-0">{editableTranslate('employee.employee_data', 'Stammdaten')}</h4>

                      <div>
                        {
                          (employee && !showEmployeeDataEditForm) ? <>
                            <Button onClick={openDeactivateModal} variant="secondary" className={`mr-2 ${hasPermissionTo('employee_management.manage_employees.*', permissions) && canManageAssignments ? '' : 'invisible'}`} data-testid="deactivate-button"><i className="bi bi-person-x text-normal"></i> {editableTranslate('employee_management.deactivate', 'Deaktivieren')}</Button>
                            <Button onClick={(e) => handleEmployeeDataAction(e, employee)} variant="secondary" className={hasPermissionTo('employee_management.manage_employees.*', permissions) ? '' : 'invisible'} data-testid="edit-button"><i className="bi bi-pencil-square text-normal"></i> {editableTranslate('global.edit')}</Button>
                          </> : <>
                            <Button
                              onClick={() => clearFormAndCallCancel()}
                              variant="secondary"
                              className="mr-2" data-testid="cancel-button"
                            >
                              {editableTranslate("button.cancel")}
                            </Button>
                            <Button
                              type="submit"
                              variant="primary"
                              loading={loading} data-testid="save-button"
                            >
                              {editableTranslate("button.save")}
                            </Button>
                          </>
                        }
                      </div>
                    </div>
                    <div>
                      <div className="employee-data-card-div" key={'basic_data'}>
                        <div className="card-setting-header d-flex justify-content-between align-items-center">
                          <span>{editableTranslate('employee.basic_data', 'Personaldaten')}</span>
                          <div>
                            {
                              employee && employee.is_example && <i className="bi bi-flag" data-tooltip-id="example-employee-tooltip"></i>
                            }
                            {
                              showEmployeeDataEditForm && <span className="ml-3"><span className="text-default">*</span> {editableTranslate('user_import.required')}</span>
                            }
                          </div>
                        </div>
                        <hr className="mt-2" />
                        <div className="card-setting-body mt-3">
                          <div className="row">
                            {
                              (employee && !showEmployeeDataEditForm) ? <Fragment>
                                {
                                  [...fixedFieldsData].map((field, i) => {
                                    const translatedName = field?.custom_name || editableTranslate(field.translation_key, field.name);
                                    let val = '';

                                    if (employee.hasOwnProperty(field.key) && employee[field.key] !== undefined && employee[field.key] !== null) {
                                      val = employee[field.key];
                                    } else if (employee.information.hasOwnProperty(field.name) && employee.information[field.name] !== undefined && employee.information[field.name] !== null) {
                                      val = employee.information[field.name];
                                    }

                                    if (val === true) val = translate('form.yes', 'Ja');
                                    else if (val === false) val = translate('form.no', 'nein');
                                    else if (field.key === 'trend') {
                                      val = translate('field.trend.' + val, val);
                                    }
                                    else if (field.key === 'gender') {
                                      val = translateGenderValue(val, translate);
                                    }
                                    else if (field.key === 'employee_group') {
                                      val = employee?.employee_group?.name || '-';
                                    }

                                    if (empty(val)) val = '-';

                                    return <div key={i} className="col-6 col-xl2-4 d-flex justify-content-between p-0">
                                      <div className="col card-body-key">{translatedName}</div>
                                      <div className={`col card-body-value ${field.key === 'email' ? 'use-ellipsis' : ''}`} data-tooltip-id={field.key === 'email' ? `employee-email-${employee.hash_id}` : ''}>{val}</div>
                                      {
                                        (field.key === 'email' && val !== '-') && <ReactTooltip id={`employee-email-${employee.hash_id}`} place="bottom" content={val} />
                                      }
                                    </div>
                                  })
                                }
                              </Fragment> : <Fragment>
                                {
                                  isInternalTenant && <>
                                    <div className="col-xl-12 col-lg-12">
                                      <div className="form-group">
                                        <div className="form-check">
                                          <input type="checkbox" className="form-check-input" id="is_example" {...register("is_example")} data-testid="is-example-checkbox" />
                                          <label className="form-check-label ml-2" htmlFor="is_example">
                                            {getLabel('field.is_example', 'Example Employee')}
                                          </label>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                }
                                {fixedFieldsData.map((field, i) => {
                                  const translatedName = getLabel(field.translation_key, field.name, field?.custom_name);

                                  if (employmentDateBoth) {
                                    if (field.key === 'employment_exit_date') return <Fragment key={i}></Fragment>;
                                    if (field.key === 'employment_entry_date') {
                                      return <div key={i} className="col-6 col-xl2-4">
                                        <div className="form-group">
                                          <label className="peb-field__label">{getLabel('field.employment_entry_exit_date')}</label>
                                          <div className="d-flex align-items-center">
                                            <DateInput error={!!errors?.employment_entry_date}
                                              {...register('employment_entry_date')} testId="employment-entry-date-input" />
                                            <div className="mx-2">-</div>
                                            <DateInput error={!!errors?.employment_exit_date}
                                              {...register('employment_exit_date')} testId="employment-exit-date-input" />
                                          </div>
                                          {(errors?.employment_entry_date || errors?.employment_exit_date) && (
                                            <div className="invalid-feedback d-block">
                                              {[errors?.employment_entry_date?.message || errors?.employment_exit_date?.message].filter(m => m).join(', ')}
                                            </div>
                                          )}
                                        </div>
                                      </div>;
                                    }
                                  }

                                  let label = (
                                    <>
                                      <label className="peb-field__label">
                                        {translatedName}
                                        <span
                                          className={`peb-field__req ${[
                                            "email",
                                            "employment_exit_date",
                                            "employment_entry_date",
                                          ].includes(field.key) && "d-none"
                                            }`}
                                        >
                                          *
                                        </span>
                                      </label>
                                    </>
                                  );
                                  let input = (
                                    <Field error={errors[field.key]?.message}>
                                      {field.key.includes("date") ? (
                                        <DateInput
                                          error={!!errors[field.key]}
                                          {...register(field.key)}
                                          maxLength={255}
                                        />
                                      ) : (
                                        <Input
                                          type="text"
                                          error={!!errors[field.key]}
                                          autoComplete="on"
                                          {...register(field.key)}
                                          maxLength={255}
                                        />
                                      )}
                                    </Field>
                                  );

                                  if (field.key === 'employee_group') {
                                    input = (
                                      <>
                                        <Controller
                                          name="employee_group_hash_id"
                                          control={control}
                                          render={({ field: selectField }) => (
                                            <div style={{ display: 'contents' }} data-testid="employee-group-hash-id-select">
                                              <Select
                                                {...selectField}
                                                error={!!errors?.employee_group_hash_id}
                                                options={groups ? groups.map(group => ({ value: group.hash_id, label: group.name })) : []}
                                                value={groups ? groups.filter(group => group.hash_id === selectField.value).map(group => ({ value: group.hash_id, label: group.name }))[0] || null : null}
                                                onChange={(option) => selectField.onChange(option ? option.value : '')}
                                                isClearable
                                                placeholder={translate('form.select_placeholder')}
                                              />
                                            </div>
                                          )}
                                        />
                                      </>
                                    )
                                  } else if (field.key === 'gender') {
                                    input = <GenderSelect control={control} errors={errors} value={watchGender} acceptedValues={field.accepted_values} />;
                                  }

                                  if (field.key === "exclude_from_payout") {
                                    // checkbox input
                                    label = (
                                      <>
                                        <label className="peb-field__label">
                                          {getLabel('field.manual_exclusion', 'Manual Exclusion')}
                                        </label>
                                      </>
                                    );
                                    input = (
                                      <>
                                        <div>
                                          <div className="form-check">
                                            <input
                                              type="checkbox"
                                              className="form-check-input"
                                              data-testid={`state-employee-field-${field.key}-checkbox`}
                                              {...register(field.key)}
                                            />
                                            <label className="form-check-label ml-2">
                                              {translatedName}
                                            </label>
                                          </div>

                                          {watchExcludeFromPayout && (
                                            <>
                                              <div className={`form-group mt-2`}>
                                                <Field
                                                  label={editableTranslate("field.exclude_from_payout_reason", "Begründung")}
                                                  required
                                                  error={errors["exclude_from_payout_reason"]?.message}
                                                >
                                                  <Input
                                                    type="text"
                                                    error={!!errors["exclude_from_payout_reason"]}
                                                    {...register("exclude_from_payout_reason")}
                                                    maxLength={255} data-testid="exclude-from-payout-reason-input"
                                                  />
                                                </Field>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </>
                                    );
                                  } else if (field.key === 'individual_pay_factor') {
                                    input = (
                                      <>
                                        <InputAffix
                                          prefix={<input type="checkbox" className="mr-2" {...register('uses_individual_pay_factor')} data-testid="uses-individual-pay-factor-checkbox" />}
                                          error={!!errors?.individual_pay_factor}
                                          readOnly={!watchUsesIndividualPayFactor}
                                        >
                                          <Input type="text" error={!!errors?.individual_pay_factor} {...register('individual_pay_factor')} readOnly={!watchUsesIndividualPayFactor} maxLength={11} data-testid="individual-pay-factor-input" />
                                        </InputAffix>
                                        {errors?.individual_pay_factor && <div className="invalid-feedback d-block">{errors.individual_pay_factor.message}</div>}
                                      </>
                                    );
                                  } else if (field.key === "trend") {
                                    // radio input
                                    input = (
                                      <>
                                        <div>
                                          <div className="form-check">
                                            <input
                                              type="radio"
                                              className="form-check-input"
                                              value="no"
                                              data-testid="state-employee-field-trend-no"
                                              {...register(field.key)}
                                            />
                                            <label className="form-check-label ml-2">
                                              {editableTranslate("field.trend.no", "Nein")}
                                            </label>
                                          </div>
                                          <div className="form-check">
                                            <input
                                              type="radio"
                                              className="form-check-input"
                                              value="yes_calculated_from_budget"
                                              data-testid="state-employee-field-trend-yes-calculated-from-budget"
                                              {...register(field.key)}
                                            />
                                            <label className="form-check-label ml-2">
                                              {editableTranslate("field.trend.yes_calculated_from_budget", "Ja, aus dem Budget berechnet")}
                                            </label>
                                          </div>
                                          <div className="form-check">
                                            <input
                                              type="radio"
                                              className="form-check-input"
                                              value="yes_manual_value"
                                              data-testid="state-employee-field-trend-yes-manual-value"
                                              {...register(field.key)}
                                            />
                                            <label className="form-check-label ml-2">
                                              {editableTranslate("field.trend.yes_manual_value", "Ja, manuelle Eingabe")}
                                            </label>
                                          </div>
                                        </div>

                                        {watchTrend === "yes_manual_value" && (
                                          <>
                                            <div className={`form-group mt-2`}>
                                              <Field
                                                label={editableTranslate("field.trend.yes_manual_value.value", "Wert")}
                                                required
                                                error={errors["manual_average_value"]?.message}
                                              >
                                                <Input
                                                  type="text"
                                                  error={!!errors["manual_average_value"]}
                                                  {...register("manual_average_value")}
                                                  maxLength={24} data-testid="manual-average-value-input"
                                                />
                                              </Field>
                                            </div>
                                          </>
                                        )}
                                      </>
                                    );
                                  }

                                  return (
                                    <div key={i} className="col-6 col-xl2-4">
                                      <div className="form-group">
                                        {label}
                                        {input}
                                      </div>
                                    </div>
                                  );
                                })}
                              </Fragment>
                            }

                            {
                              stateEmployeeSectionsFiltered && stateEmployeeSectionsFiltered.map((section, i) => <Fragment key={i}>
                                {
                                  (employee && !showEmployeeDataEditForm) ? (
                                    section.fields.map((field, j) => {
                                      let val = '-';

                                      if (field.field_type === 'compound') {
                                        let hasValue = false;
                                        let firstCheckboxAndFalse = false; // if starts with checkbox, dont display anything if unchecked
                                        const arr = field.components.map((component, i) => {
                                          if (component.type === 'input') {
                                            let v = employee?.information?.[component.identifier] || '';
                                            if (component?.stored_in_table === 'employees') {
                                              v = (employee?.[component.identifier] || '');
                                            }

                                            if (component?.input_type === 'integer' && v !== '') {
                                              v = removeTrailingZeros(v);
                                            }

                                            if (component?.input_type === 'boolean') {
                                              if (['1', true].includes(v)) {
                                                v = translate('form.yes', 'Ja');
                                              } else {
                                                v = null;

                                                if (i === 0) firstCheckboxAndFalse = true;
                                              }
                                            }
                                            if (!empty(v) && !firstCheckboxAndFalse) hasValue = true;
                                            return v;
                                          } else if (component.type === 'text') {
                                            return translate(component?.text_translation_key);
                                          }
                                          return '';
                                        });
                                        if (hasValue) val = arr.join(' ');
                                      } else {
                                        val = (employee?.information?.[field.identifier] || '-');

                                        if (field?.stored_in_table === 'employees') {
                                          val = (employee?.[field.identifier] || '-');
                                        }

                                        if ((field?.field_type === 'integer' || field?.field_type === "float" || field?.field_type === 'numeric' || field?.field_type.includes("decimal")) && val !== '-') {
                                          val = removeTrailingZeros(val);
                                        }

                                        if (field.field_type === 'boolean') {
                                          if (['1', true].includes(val)) {
                                            val = translate('form.yes', 'Ja');
                                          } else {
                                            val = '-';
                                          }
                                        }

                                        if (field?.identifier === 'working_hours' && softwareSettings?.working_hours_display === 'parttime_fulltime' && val !== '-') {
                                          if (parseFloat(formatNumberTo(val, '.')) >= 100) {
                                            val = translate('general_settings.balanced_scorecard.working_hours_display.fulltime');
                                          } else {
                                            val = translate('general_settings.balanced_scorecard.working_hours_display.parttime');
                                          }
                                        } else if (field?.suffix && val !== '-') {
                                          val = val + field?.suffix;
                                        }

                                        if (field.identifier === 'previous_evaluation_results') {
                                          val = employee?.previous_evaluation_results?.regular_evaluation?.result ? getLabeledValue(employee?.previous_evaluation_results?.regular_evaluation?.result) : '-';
                                        } else if (!empty(val) && field.identifier === 'pay_grade') {
                                          val = employee?.pay_grade_label || val;
                                        } else if (!empty(val) && field.identifier === 'job_differs_from_classification') {
                                          const resolvedLabel = getEmployeeInformationMeta(field.identifier)?.resolved_label;
                                          if (resolvedLabel) val = resolvedLabel;
                                        }
                                        // else if (!empty(val) && field.identifier === 'job_differs_from_classification') {
                                        //   const obj = fieldValues?.job_differs_from_classification?.find(o => o.value === val);
                                        //   if (obj) val = obj.label;
                                        // }
                                      }

                                      return <div key={j} className={`${section.fields.length > 0 ? 'col-6 col-xl2-4 d-flex justify-content-between' : 'col'} p-0`}>
                                        <div className={`col ${section.fields.length > 0 ? 'card-body-key' : ''}`}>{field?.custom_name || editableTranslate(field.translation_key, field.name)}</div>
                                        <div className="col card-body-value" dangerouslySetInnerHTML={{ __html: sanitize(val) }} />
                                      </div>
                                    })
                                  ) : (
                                    section.fields.map((field, j) => <div key={j} className={`${section.fields.length > 0 ? 'col-6 col-xl2-4' : 'col'}`}>
                                      <StateEmployeeField field={field} employee={employee} form={{ register, watch, setValue, errors, control }} forEvaluator={false} />
                                    </div>)
                                  )
                                }
                              </Fragment>)
                            }
                          </div>
                        </div>
                      </div>

                      {
                        freeFieldsData.length > 0 && <div className="employee-data-card-div mt-3" key={'additional_data'}>
                          <div className="card-setting-header d-flex justify-content-between align-items-center">
                            <span>{editableTranslate('employee.additional_data', 'Freifelder')}</span>
                          </div>
                          <hr className="mt-2" />
                          <div className="card-setting-body mt-3">
                            <div className="row">
                              {
                                !showEmployeeDataEditForm ? freeFieldsData.map((field, i) => {
                                  const translatedName = field?.custom_name || editableTranslate(field.translation_key, field.name);
                                  let val = employee?.information?.[field.name] || employee?.information?.[field.slug] || '-';

                                  return <div key={i} className="col-6 col-xl2-4 d-flex justify-content-between p-0">
                                    <div className="col card-body-key">{translatedName}</div>
                                    <div className="col card-body-value">{val}</div>
                                  </div>
                                }) : [...freeFieldsData].map((field, i) => {
                                  const fieldError = field?.source === 'state_employee_config' ? errors?.[field.slug] : errors?.employee_information?.[field.name];
                                  return (
                                    <div key={i} className="col-6 col-xl2-4">
                                      <div className="form-group">
                                        <Field
                                          label={field?.custom_name || editableTranslate(field.translation_key, field.name)}
                                          error={fieldError?.message}
                                        >
                                          <Input
                                            type="text"
                                            error={!!fieldError}
                                            {...register(field?.source === 'state_employee_config' ? field.slug : `employee_information.${field.name}`)}
                                            maxLength={255}
                                          />
                                        </Field>
                                      </div>
                                    </div>
                                  );
                                })
                              }
                            </div>
                          </div>
                        </div>
                      }
                    </div>
                  </form>
                </div>
              </div>

              {
                employee && <div className="col-auto employee-balancescorecard-card-col">
                  <div className="h-100">
                    <div className={`employee-data-card ` + (employee.exclude_from_payout ? ' exclude-from-payout' : '')}>
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <h4 className="mb-0">{editableTranslate('state_employee.rating_section_title')}
                        </h4>
                        {
                          !employee.exclude_from_payout && <Button variant="secondary" className={`add-balanced-scorecard-btn ml-3 ${(employeeBalancedScorecards.length < maxBalancedScorecard && hasPermissionTo('employee_management.manage_balanced_scorecards.create', permissions)) ? '' : 'invisible'} `} onClick={handleAddBalancedScorecard} data-testid="add-scorecard-button"><i className="bi bi-plus text-normal"></i> {editableTranslate('employee_management.add_balanced_scorecard', 'Add')}</Button>
                        }
                      </div>
                      {
                        employeeBalancedScorecards.map((bs, i) => <BalancedScorecard key={i} index={i} employee={employee} data={bs} total={employeeBalancedScorecards.length} fetchEmployees={fetchEmployees} balancedScorecardOpened={balancedScorecardOpened} setBalancedScorecardOpened={setBalancedScorecardOpened} handleCancelAddBalancedScorecard={handleCancelAddBalancedScorecard} permissions={permissions} setEmployeeLkDirty={setEmployeeLkDirty} getLabel={getLabel} lkWeightTotal={lkWeightTotal} evaluationFlowOptions={evaluationFlowOptions} isInternalTenant={isInternalTenant} />)
                      }

                      <ReactTooltip id={`lk-last-updated-tooltip`} place="bottom" />
                      <ReactTooltip id={`complaint-resolved-tooltip`} place="bottom" />
                      <ReactTooltip id={`complaint-unlock`} place="bottom" content={translate('tooltips.complaint.unlock', 'Entsperren')} />
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          {
            !employee && <hr className="mb-0" />
          }

          <EmployeeDeactivationModal
            show={showDeactivateModal}
            loading={loadingDeactivate}
            employeeCount={1}
            finalAssignmentCount={finalAssignmentCount}
            requiresFinalAssignmentConfirmation={finalAssignmentCount !== null}
            onCancel={closeDeactivateModal}
            onConfirm={() => handleEmployeeDeactivate(finalAssignmentCount !== null)}
            testId="employee-deactivation"
          />
        </td>
      </tr>
    }
  </>
}
